import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import { Box, LinearProgress, Icon, Card, Button, Stack, TextField, IconButton, CardActions, InputAdornment, Tooltip } from '@mui/material';

import Mail from '@mui/icons-material/Mail';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'

import * as yup from 'yup';
import { withFormik } from 'formik';
import loginValidations from '../../schemas/LoginSchema';

import Logo from "../../images/iss.png";
import BG from "../../images/LoginPng.png";

import { ThemeModeContext, apiRoute, frontendRoute } from '../../App';
import OtpVerificationForm from './OtpVerificationForm';

const Login = (props) => {
    const { mode } = useContext(ThemeModeContext);

    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loadingLogin, setLoadingLogin] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [userId, setUserId] = useState(0);
    const [otp, setOtp] = useState("");
    const [disableTryAgainButton, setDisableTryAgainButton] = useState(false);
    const [loginSuccessful, setLoginSuccessful] = useState(false);
    const [loginFailure, setLoginFailure] = useState(false);
    const tryAgainButtonTime = 60000;

    const navigate = useNavigate();

    const forgotPasswordRoute = `${frontendRoute}/forgotpassword`;

    const handleCancel = () => {
        setError("")
        setShowOtp(false);
    };

    const login = async (e) => {
        e.preventDefault();
        setError(false);
        setLoginFailure(false);
        try {
            setLoadingLogin(true);
            const res = await axios.post(`${apiRoute}/api/auth/login`, values);
            if (!res.data.isMFAEnabled) {
                const token = res?.data?.token;
                const timeZone = res?.data?.timeZone;
                localStorage.setItem('token', token);
                localStorage.setItem('timeZone', timeZone);
                // Token is valid, set it as a default Authorization header for axios
                axios.defaults.headers.common = {
                    'Authorization': `Bearer ${token}`
                };
                setTimeout(() => {
                    setLoginSuccessful(true);
                }, 1000);

                setTimeout(() => {
                    setLoadingLogin(false);
                    navigate('/');
                }, 2000);
            } else {
                setUserId(res?.data.userId);
                setLoadingLogin(false);
                setShowOtp(true);
            }
        } catch (err) {
            console.log('Login Failed', err);
            setLoadingLogin(false);
            setLoginSuccessful(false);
            setLoginFailure(true);
            setError(err?.response?.data);
        }
    };

    const confirmOtp = async () => {
        try {
            setLoadingLogin(true);
            const body = {
                userId: userId,
                otpCode: otp
            }
            const res = await axios.post(`${apiRoute}/api/auth/confirmotp`, body);
            if (res.status === 200 && res.data.isValid) {
                const token = res?.data?.token
                localStorage.setItem('token', res?.data?.token);
                const timeZone = res?.data?.timeZone;
                localStorage.setItem('timeZone', timeZone);
                // Token is valid, set it as a default Authorization header for axios
                axios.defaults.headers.common = {
                    'Authorization': `Bearer ${token}`
                };
                setLoginSuccessful(true);
                setTimeout(() => {
                    setLoadingLogin(false);
                    setShowOtp(false);
                    navigate('/');
                }, 1000)

            } else {
                toast.error(`${res.data.message}`, {
                    position: 'top-right',
                    autoClose: 2000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'colored',
                });
                if (res.data.isLocked) {
                    navigate('/');
                }
            }
        } catch (err) {
            console.log('Login Failed', err);
            setLoadingLogin(false);
            setError(err?.response?.data);
        }
    };

    const regenerateOtp = async () => {
        try {
            setLoadingLogin(true);
            const res = await axios.post(`${apiRoute}/api/auth/regenerateOTP?userId=${userId}`);
            if (res.status === 200 && res.data.isValid) {
                setLoadingLogin(false);
                setDisableTryAgainButton(true);
                setTimeout(() => setDisableTryAgainButton(false), tryAgainButtonTime);
                toast.success(`${res.data.message}`, {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
            } else {
                setLoadingLogin(false);
                toast.error(`${res.data.message}`, {
                    position: 'top-right',
                    autoClose: 2000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'colored',
                });
            }
        } catch (err) {
            console.log('Login Failed', err);
            setLoadingLogin(false);
            setError(err?.response?.data);
        }
    };



    const handleClickShowPassword = (e) => {
        setShowPassword((show) => !show);
    };

    const handleMouseDownPassword = (e) => {
        e.preventDefault();
    };

    const {
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
    } = props;

    const caseSensitiveTheme = createTheme({
        palette: {
            mode,
        },
        typography: {
            allVariants: {
                textTransform: 'none',
            },
        },
        components: {
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        textTransform: 'none', // For input fields
                    },
                },
            },
        },
    });

    return (<>
        <ThemeProvider theme={caseSensitiveTheme}>
            <Box sx={{
                backgroundImage: `url(${BG})`,
                backgroundSize: '50%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '50% -1vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                backgroundColor: mode === 'light' ? 'inherit' : '#313131'
            }}>
                {/* loginContainer */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    justifyContent: 'center',
                    backgroundSize: 'contain'
                }}>
                    {/* loginBox */}
                    {/*<Box sx={{*/}
                    {/*    position: 'relative',*/}
                    {/*    zIndex: '0',*/}
                    {/*    borderRadius: '5px',*/}
                    {/*    filter: 'drop-shadow(0px 1px 0.5px black)',*/}
                    {/*    minHeight: '38vh',*/}
                    {/*    minWidth: '50vh',*/}
                    {/*    backgroundColor: mode === 'dark' ? 'white' : '#f4f7fa'*/}
                    {/*}}>*/}
                    {/* logoBox */}
                    <Box sx={{
                        margin: '0 auto',
                        position: 'relative',
                        zIndex: '1',
                        textAlign: 'center',
                        borderRadius: '10px 10px 0 0',
                        filter: 'drop-shadow(0px 1px .5px gray)',
                        backgroundColor: 'steelBlue',
                        p: '1.5rem',
                        color: 'white',
                        px: 15
                    }}>
                        <h2 style={{ marginBottom: 0 }}
                        >
                            Secure Collect</h2>
                        <h6 style={{ font: 'caption' }}>
                            <em>Collection and Distribution System</em>
                        </h6>

                    </Box>
                    {!showOtp ? <form onSubmit={login}>
                        <Card>
                            <Box px={15} pt={2.5} sx={{
                                textAlign: 'center',
                                backgroundColor: 'white'
                            }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box mb={5}>
                                        <TextField
                                            id='email'
                                            label='Email'
                                            type='email'
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            helperText={touched.email ? errors.email : ''}
                                            error={touched.email && Boolean(errors.email)}
                                            margin="dense"
                                            variant="standard"
                                            slotProps={{
                                                inputLabel: {
                                                    sx: {
                                                        color: 'grey',
                                                    },
                                                    shrink: true
                                                },
                                                input: {
                                                    sx: {
                                                        backgroundColor: '#e8f0fe',
                                                        color: mode === 'dark' ? 'black' : 'inherit',
                                                        width: '100%',
                                                        mr: '1.5vh',
                                                        pr: '1vh'
                                                    },
                                                    endAdornment: (
                                                        <InputAdornment position='end'>
                                                            <Icon
                                                                //edge='end'
                                                                sx={{ color: 'grey' }}>
                                                                <Mail />
                                                            </Icon>
                                                        </InputAdornment>
                                                    )
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <TextField
                                            id='password'
                                            label='Password'
                                            type={showPassword ? 'text' : 'password'}
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            helperText={touched.password ? errors.password : ''}
                                            error={touched.password && Boolean(errors.password)}
                                            margin="dense"
                                            variant="standard"
                                            color="primary"
                                            slotProps={{
                                                inputLabel: {
                                                    sx: {
                                                        color: 'grey',
                                                    },
                                                    shrink: true
                                                },
                                                input: {
                                                    sx: {
                                                        backgroundColor: '#e8f0fe',
                                                        color: mode === 'dark' ? 'black' : 'inherit'
                                                    },
                                                    endAdornment: (
                                                        <InputAdornment position='end'>
                                                            <Tooltip title={`CLICK TO ${showPassword ? "HIDE" : "VIEW"}`} placement="top" enterDelay="2000">
                                                                <IconButton
                                                                    aria-label='toggle password visibility'
                                                                    onClick={handleClickShowPassword}
                                                                    onMouseDown={handleMouseDownPassword}
                                                                    //edge='end'
                                                                    sx={{ color: 'grey' }}
                                                                >
                                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    )
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ maxWidth: '40vh', margin: '0 auto', textAlign: 'center' }}>
                                    {error && <Box sx={{ color: 'red', mt: '1vh' }}>{error}</Box>}
                                </Box>
                                <Box sx={{
                                    padding: '2vh 0 1vh 0',
                                    filter: 'drop-shadow(0px 1px .5px gray)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}>
                                    <CardActions sx={{ width: '100%' }}>
                                        <Button
                                            type={loadingLogin ? 'button' : loginSuccessful ? 'button' : 'submit'}
                                            variant='contained'
                                            sx={{
                                                width: '80%',
                                                m: '0 auto',
                                                backgroundColor: loginSuccessful ? '#50C878' : loginFailure ? '#ae0e0e' : 'steelblue',
                                                opacity: loadingLogin ? '90%' : loginSuccessful ? '90%' : '100%',
                                                color: 'white'
                                            }}
                                        >
                                            {loginSuccessful ? "WELCOME" : loginFailure ? "TRY AGAIN" : "LOGIN"}
                                        </Button>
                                    </CardActions>
                                </Box>
                                <Box pb={3.5}>
                                    <Link to="/forgotpassword" style={{ textDecoration: 'inherit', color: mode === 'dark' ? '#1e1e1e' : 'inherit' }}>
                                        Forgot Password?
                                    </Link>
                                </Box>
                            </Box>
                            {
                                loadingLogin && (
                                    <LinearProgress sx={{ width: '99.5%', m: '0 auto' }} color={loginSuccessful ? 'success' : loginFailure ? 'error' : 'primary'} />
                                )
                            }
                            <Box
                                sx={{
                                    backgroundColor: '#f4f7fa'
                                }}
                            >
                                <Stack direction="row" spacing={1} sx={{ mx: 'auto', justifyContent: 'center' }}>
                                    <h6 style={{ marginTop: '1.55rem', 'font-size': '85%', font: 'caption', color: mode === 'dark' ? '#1e1e1e' : 'inherit' }}><em>Powered by:</em></h6>
                                    <img
                                        src={Logo}
                                        alt="Interactive Software Solutions Logo"
                                        width='55rem'
                                    />
                                </Stack>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    color: mode === 'dark' ? '#1e1e1e' : 'inherit'
                                }}
                                >
                                    <h4
                                        style={{
                                            'font-size': '1rem',
                                            font: 'caption'
                                        }}
                                    >
                                        <strong>INTERACTIVE SOFTWARE SOLUTIONS</strong>
                                    </h4>
                                </Box>
                            </Box>
                        </Card>
                    </form> :
                        <OtpVerificationForm
                            otp={otp}
                            setOtp={setOtp}
                            setShowOtp={setShowOtp}
                            confirmOtp={confirmOtp}
                            tryAgain={regenerateOtp}
                            disableTryAgainButton={disableTryAgainButton}
                            setDisableTryAgainButton={setDisableTryAgainButton}
                            handleCancel={handleCancel}
                            loginSuccessful={loginSuccessful}
                            loadingLogin={loadingLogin}
                        />}

                </Box>
                {/*</Box>*/}
            </Box>
        </ThemeProvider >
    </>
    )
};

const Form = withFormik({
    mapPropsToValues: ({
        email,
        password
    }) => {
        return {
            email: email || '',
            password: password || ''
        }
    },

    validationSchema: yup.object().shape(loginValidations),

    handleSubmit: (values, { setSubmitting }) => {
        setTimeout(() => {
            toast.info(JSON.stringify(values, null, 2), {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "colored",
            });
            setSubmitting(false);
        }, 1000);
    }
})(Login);

export default Form;